import React, { useCallback, useEffect, useRef, useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { InputNumber } from "antd";
import type { FormInstance } from "antd";
import {
  Form,
  Input,
  ToggleSwitch,
} from "../../../../components/shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { BannerIn } from "../types";
import type { FreeformBannerLayout } from "../Campaigns/CreateCampaignWizard/toWizardCampaignIn";
import { DEFAULT_FREEFORM_BANNER_LAYOUT } from "../Campaigns/CreateCampaignWizard/toWizardCampaignIn";
import styles from "../stylesDynamicBonuses.module.css";
import BannerDeeplinkField from "./BannerDeeplinkField";
import ColorInput from "./ColorInput";
import ColorListInput from "./ColorListInput";
import UploadImageModal from "./UploadImageModal";

type NamePath = (string | number)[];

type Props = {
  form: FormInstance;
  /** Path to `steps[n].banner`. */
  bannerPath: NamePath;
  /** Relative path under the Form.List item (e.g. `["banner"]`). */
  bannerItemPath: NamePath;
  /** Path to `steps[n].freeform_layout`. */
  layoutPath: NamePath;
  /** Optional content rendered below the live preview (e.g. display locations). */
  belowPreview?: React.ReactNode;
  /**
   * Render-prop escape hatch (BONENG-1189): compose freeform fields and the
   * live preview into the step grid's Display section instead of a standalone
   * full-width block below Rules.
   */
  children?: (parts: {
    formFields: React.ReactNode;
    previewColumn: React.ReactNode;
  }) => React.ReactNode;
  /**
   * Require an image URL (v2 Display Info Banner). Flag-off Display Banner
   * omits this so an empty image still saves.
   */
  imageRequired?: boolean;
};

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/;

const colorRule = {
  pattern: HEX_COLOR_PATTERN,
  message: "Use #RRGGBB or #RRGGBBAA",
};

const colorListRule = {
  validator: (_: unknown, value?: string[]) => {
    if (!value?.length || value.every((color) => HEX_COLOR_PATTERN.test(color))) {
      return Promise.resolve();
    }
    return Promise.reject(
      new Error("Each stop must be a valid #RRGGBB or #RRGGBBAA color"),
    );
  },
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const normalizeLayout = (
  layout?: FreeformBannerLayout,
): FreeformBannerLayout => ({
  image: {
    x: layout?.image?.x ?? DEFAULT_FREEFORM_BANNER_LAYOUT.image.x,
    y: layout?.image?.y ?? DEFAULT_FREEFORM_BANNER_LAYOUT.image.y,
    width: layout?.image?.width ?? DEFAULT_FREEFORM_BANNER_LAYOUT.image.width,
  },
  button: {
    x: layout?.button?.x ?? DEFAULT_FREEFORM_BANNER_LAYOUT.button.x,
    y: layout?.button?.y ?? DEFAULT_FREEFORM_BANNER_LAYOUT.button.y,
    width: layout?.button?.width ?? DEFAULT_FREEFORM_BANNER_LAYOUT.button.width,
    height:
      layout?.button?.height ?? DEFAULT_FREEFORM_BANNER_LAYOUT.button.height,
  },
});

const freeformClaimStyle = (
  banner: BannerIn | undefined,
  layout: FreeformBannerLayout,
  buttonX: number,
  buttonY: number,
): React.CSSProperties => {
  const claim = banner?.claim;
  const claimGradient = (claim?.background_color ?? []).filter(Boolean);
  return {
    left: `${buttonX}%`,
    top: `${buttonY}%`,
    width: `${layout.button.width}%`,
    height: `${layout.button.height}%`,
    ...(claimGradient.length
      ? { background: `linear-gradient(92deg, ${claimGradient.join(", ")})` }
      : {}),
    ...(claim?.text_color ? { color: claim.text_color } : {}),
    ...(claim?.border_color
      ? {
          borderColor: claim.border_color,
          borderStyle: "solid",
          borderWidth: "1px",
        }
      : { border: "none" }),
  };
};

export type FreeformBannerPreviewProps = {
  banner?: BannerIn;
  layout?: FreeformBannerLayout;
  /** Override live drag position while editing. */
  buttonPosition?: { x: number; y: number };
  interactive?: boolean;
  onButtonPointerDown?: (event: React.PointerEvent<HTMLElement>) => void;
  canvasRef?: React.Ref<HTMLDivElement>;
};

/** Read-only (or drag-enabled) freeform banner canvas — shared by editor + review. */
export const FreeformBannerPreview: React.FC<FreeformBannerPreviewProps> = ({
  banner = {},
  layout: layoutProp,
  buttonPosition,
  interactive = false,
  onButtonPointerDown,
  canvasRef,
}) => {
  const layout = normalizeLayout(layoutProp);
  const buttonVisible = banner.claim?.visible !== false;
  const imageUrl = banner.image?.url ?? undefined;
  const buttonLabel =
    banner.claim?.text?.en?.trim() ||
    AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_PLACEHOLDER;
  const buttonX = buttonPosition?.x ?? layout.button.x;
  const buttonY = buttonPosition?.y ?? layout.button.y;
  const claimStyle = freeformClaimStyle(banner, layout, buttonX, buttonY);

  return (
    <div
      ref={canvasRef}
      className={styles.freeformBannerCanvas}
      role="img"
      aria-label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_PREVIEW_LABEL}
      data-testid="freeform-banner-preview"
    >
      <div
        className={styles.freeformBannerImageLayer}
        style={{
          left: `${layout.image.x}%`,
          top: `${layout.image.y}%`,
          width: `${layout.image.width}%`,
        }}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" draggable={false} />
        ) : (
          <div className={styles.freeformBannerImagePlaceholder}>
            {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_DROP_IMAGE}
          </div>
        )}
      </div>

      {buttonVisible ? (
        interactive ? (
          <button
            type="button"
            className={styles.freeformBannerCta}
            style={claimStyle}
            onPointerDown={onButtonPointerDown}
          >
            {buttonLabel}
          </button>
        ) : (
          <div
            className={`${styles.freeformBannerCta} ${styles.freeformBannerCtaReadonly}`}
            style={claimStyle}
          >
            {buttonLabel}
          </div>
        )
      ) : null}
    </div>
  );
};

/**
 * Freeform canvas for standalone marketing banners (BONENG-813): place an
 * image and a fully configurable CTA button anywhere on the banner.
 */
const FreeformBannerEditor: React.FC<Props> = ({
  form,
  bannerPath,
  bannerItemPath,
  layoutPath,
  belowPreview,
  children,
  imageRequired = false,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  /** Live drag position — keeps pointer capture stable (avoids form re-render drops). */
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const dragPosRef = useRef<{ x: number; y: number } | null>(null);
  const dragListenersRef = useRef<{
    onMove: (event: PointerEvent) => void;
    onUp: () => void;
  } | null>(null);

  /**
   * Local layout is the source of truth while editing. AntD can briefly drop
   * unregistered / nested object watches; keeping state here prevents the
   * preview from snapping back to defaults.
   */
  const [layout, setLayout] = useState<FreeformBannerLayout>(() =>
    normalizeLayout(form.getFieldValue(layoutPath) as FreeformBannerLayout),
  );
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const banner =
    (Form.useWatch(bannerPath, { form, preserve: true }) as
      | BannerIn
      | undefined) ?? {};

  const buttonVisible = banner.claim?.visible !== false;

  const buttonX = dragPos?.x ?? layout.button.x;
  const buttonY = dragPos?.y ?? layout.button.y;

  const commitLayout = useCallback(
    (next: FreeformBannerLayout) => {
      layoutRef.current = next;
      setLayout(next);
      form.setFieldValue(layoutPath, next);
    },
    [form, layoutPath],
  );

  const patchButtonLayout = useCallback(
    (patch: Partial<FreeformBannerLayout["button"]>) => {
      const current = layoutRef.current;
      commitLayout({
        ...current,
        button: { ...current.button, ...patch },
      });
    },
    [commitLayout],
  );

  const clientToBannerPercent = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  const stopButtonDragListeners = useCallback(() => {
    const listeners = dragListenersRef.current;
    if (!listeners) return;
    window.removeEventListener("pointermove", listeners.onMove);
    window.removeEventListener("pointerup", listeners.onUp);
    window.removeEventListener("pointercancel", listeners.onUp);
    dragListenersRef.current = null;
  }, []);

  useEffect(() => () => stopButtonDragListeners(), [stopButtonDragListeners]);

  /**
   * Window-level listeners so React re-renders during drag don't drop
   * pointer capture / move events (the previous bug that blocked dragging).
   */
  const startButtonDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const pct = clientToBannerPercent(event.clientX, event.clientY);
      if (!pct) return;

      stopButtonDragListeners();

      const origin = {
        x: layoutRef.current.button.x,
        y: layoutRef.current.button.y,
      };
      const offsetX = pct.x - origin.x;
      const offsetY = pct.y - origin.y;
      const maxX = Math.max(0, 100 - layoutRef.current.button.width);
      const maxY = Math.max(0, 100 - layoutRef.current.button.height);

      const startPos = { x: origin.x, y: origin.y };
      dragPosRef.current = startPos;
      setDragPos(startPos);

      const onMove = (moveEvent: PointerEvent) => {
        const nextPct = clientToBannerPercent(
          moveEvent.clientX,
          moveEvent.clientY,
        );
        if (!nextPct) return;
        const next = {
          x: clamp(Math.round(nextPct.x - offsetX), 0, maxX),
          y: clamp(Math.round(nextPct.y - offsetY), 0, maxY),
        };
        dragPosRef.current = next;
        setDragPos(next);
      };

      const onUp = () => {
        stopButtonDragListeners();
        const next = dragPosRef.current ?? layoutRef.current.button;
        patchButtonLayout({ x: next.x, y: next.y });
        dragPosRef.current = null;
        setDragPos(null);
      };

      dragListenersRef.current = { onMove, onUp };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [clientToBannerPercent, patchButtonLayout, stopButtonDragListeners],
  );

  const claimItemPath = (...segments: NamePath): NamePath => [
    ...bannerItemPath,
    "claim",
    ...segments,
  ];

  const formFields = (
    <div
      className={styles.freeformBannerFields}
      data-testid="freeform-banner-editor"
    >
      <p className={styles.freeformBannerHint}>
        {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_IMAGE_HINT}
      </p>
      <div className={styles.uploadImageFieldWrap}>
        <div className={styles.bannerImageLabelRow}>
          <span className={styles.bannerImageLabelText}>
            {AppStrings.PAM_DYNAMIC_BONUS_BANNER_IMAGE_LABEL}
          </span>
          <button
            type="button"
            className={styles.uploadImageTrigger}
            onClick={() => setIsUploadOpen(true)}
          >
            <UploadOutlined />{" "}
            {AppStrings.PAM_DYNAMIC_BONUS_BANNER_UPLOAD_IMAGE}
          </button>
        </div>
        <Form.Item
          name={[...bannerItemPath, "image", "url"]}
          rules={[
            ...(imageRequired
              ? [
                  {
                    required: true,
                    whitespace: true,
                    message:
                      AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_IMAGE_REQUIRED,
                  },
                ]
              : []),
            {
              validator: (_: unknown, value?: string) => {
                const trimmed = value?.trim();
                if (
                  !trimmed ||
                  trimmed.startsWith("http://") ||
                  trimmed.startsWith("https://")
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(AppStrings.PAM_DYNAMIC_BONUS_BANNER_HTTP_URL_RULE),
                );
              },
            },
          ]}
        >
          <Input
            placeholder={AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_DROP_IMAGE}
          />
        </Form.Item>
      </div>

      <section className={styles.freeformButtonSection}>
        <div className={styles.freeformButtonSectionHeader}>
          <h4 className={styles.freeformButtonSectionTitle}>
            {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_SECTION}
          </h4>
          <Form.Item
            name={claimItemPath("visible")}
            getValueProps={(value: boolean | undefined) => ({
              value: value !== false,
            })}
            style={{ marginBottom: 0 }}
          >
            <ToggleSwitch
              tone="primary"
              ariaLabel={
                AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_SECTION
              }
            />
          </Form.Item>
        </div>

        <p className={styles.freeformBannerHint}>
          {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_HINT}
        </p>

        {buttonVisible ? (
          <>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_LABEL}
              name={claimItemPath("text", "en")}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: AppStrings.PAM_DYNAMIC_BONUS_BANNER_TITLE_REQUIRED,
                },
              ]}
            >
              <Input
                placeholder={
                  AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_PLACEHOLDER
                }
              />
            </Form.Item>

            <div className={styles.freeformButtonControlBlock}>
              <span className={styles.freeformButtonControlLabel}>
                {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_SIZE}
                {" / "}
                {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_POSITION}
              </span>
              <div className={styles.freeformButtonMetricsRow}>
                <label className={styles.freeformButtonPositionField}>
                  <span>
                    {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_WIDTH}
                  </span>
                  <InputNumber
                    className={styles.numberInput}
                    controls={false}
                    min={5}
                    max={100}
                    value={layout.button.width}
                    onChange={(value) => {
                      const width = clamp(Number(value ?? 5), 5, 100);
                      patchButtonLayout({
                        width,
                        x: clamp(
                          layoutRef.current.button.x,
                          0,
                          Math.max(0, 100 - width),
                        ),
                      });
                    }}
                  />
                </label>
                <label className={styles.freeformButtonPositionField}>
                  <span>
                    {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_HEIGHT}
                  </span>
                  <InputNumber
                    className={styles.numberInput}
                    controls={false}
                    min={5}
                    max={100}
                    value={layout.button.height}
                    onChange={(value) => {
                      const height = clamp(Number(value ?? 5), 5, 100);
                      patchButtonLayout({
                        height,
                        y: clamp(
                          layoutRef.current.button.y,
                          0,
                          Math.max(0, 100 - height),
                        ),
                      });
                    }}
                  />
                </label>
                <label
                  className={styles.freeformButtonPositionField}
                  data-testid="freeform-button-position"
                >
                  <span>
                    {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_POS_X}
                  </span>
                  <InputNumber
                    className={styles.numberInput}
                    controls={false}
                    min={0}
                    max={100}
                    value={Math.round(buttonX)}
                    onChange={(value) => {
                      const maxX = Math.max(
                        0,
                        100 - layoutRef.current.button.width,
                      );
                      patchButtonLayout({
                        x: clamp(Number(value ?? 0), 0, maxX),
                      });
                    }}
                  />
                </label>
                <label className={styles.freeformButtonPositionField}>
                  <span>
                    {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_POS_Y}
                  </span>
                  <InputNumber
                    className={styles.numberInput}
                    controls={false}
                    min={0}
                    max={100}
                    value={Math.round(buttonY)}
                    onChange={(value) => {
                      const maxY = Math.max(
                        0,
                        100 - layoutRef.current.button.height,
                      );
                      patchButtonLayout({
                        y: clamp(Number(value ?? 0), 0, maxY),
                      });
                    }}
                  />
                </label>
              </div>
            </div>

            <div className={styles.freeformButtonControlBlock}>
              <span className={styles.freeformButtonControlLabel}>
                {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_STYLE}
              </span>
              <div className={styles.freeformButtonStyleGrid}>
                <Form.Item
                  label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_TEXT_COLOR}
                  name={claimItemPath("text_color")}
                  rules={[colorRule]}
                >
                  <ColorInput placeholder="#09090C" />
                </Form.Item>
                <Form.Item
                  label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BORDER_COLOR}
                  name={claimItemPath("border_color")}
                  rules={[colorRule]}
                >
                  <ColorInput placeholder="#FD6F27" />
                </Form.Item>
                <Form.Item
                  label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_BACKGROUND_COLOR}
                  name={claimItemPath("background_color")}
                  rules={[colorListRule]}
                  tooltip="Add one color for solid, or multiple for a gradient"
                  className={styles.freeformButtonStyleFull}
                >
                  <ColorListInput
                    placeholder="#FC4233"
                    textAction
                    hidePreview
                    inline
                    addLabel={AppStrings.PAM_DYNAMIC_BONUS_BANNER_ADD_COLOR}
                  />
                </Form.Item>
              </div>
            </div>
          </>
        ) : null}

        <BannerDeeplinkField
          form={form}
          namePath={bannerPath}
          itemNamePath={bannerItemPath}
        />
      </section>
    </div>
  );

  const previewBlock = (
    <div className={styles.freeformBannerPreviewColumn}>
      <p className={styles.bannerDesignPreviewLabel}>
        {AppStrings.PAM_DYNAMIC_BONUS_BANNER_PREVIEW_LABEL}
      </p>
      <FreeformBannerPreview
        canvasRef={canvasRef}
        banner={banner}
        layout={layout}
        buttonPosition={{ x: buttonX, y: buttonY }}
        interactive
        onButtonPointerDown={startButtonDrag}
      />
    </div>
  );

  const previewColumn = children ? (
    <div
      className={`${styles.bannerEditorColumn} ${styles.bannerEditorPreviewColumn}`}
    >
      {previewBlock}
      {belowPreview}
    </div>
  ) : (
    previewBlock
  );

  const uploadModal = (
    <UploadImageModal
      open={isUploadOpen}
      onClose={() => setIsUploadOpen(false)}
      onUploaded={(url) =>
        form.setFieldValue([...bannerPath, "image", "url"], url)
      }
    />
  );

  if (children) {
    return (
      <>
        {children({
          formFields: (
            <div className={styles.bannerEditorColumn}>{formFields}</div>
          ),
          previewColumn,
        })}
        {uploadModal}
      </>
    );
  }

  return (
    <div className={styles.freeformBannerEditor}>
      {formFields}
      {previewColumn}
      {uploadModal}
    </div>
  );
};

export default FreeformBannerEditor;
